import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Container,
  Stack,
  CircularProgress,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Badge
} from '@mui/material';
import {
  Security,
  Info,
  PlayArrow,
  CheckCircle,
  Warning,
  Error,
  Assessment,
  Business,
  HealthAndSafety,
  AccountBalance,
  School,
  LocalHospital,
  Gavel,
  Shield,
  ExpandMore,
  Download,
  Refresh,
  CloudDownload,
  Description,
  Visibility,
  History,
  Delete as DeleteIcon,
  AccessTime,
  FileDownload
} from '@mui/icons-material';
import { useNotifications } from '../contexts/NotificationContext';

// Render a framework icon by string name
const ICON_MAP = { Security, Shield, AccountBalance, Gavel, Assessment, LocalHospital, Business };
const FrameworkIcon = ({ name, ...props }) => {
  const Icon = ICON_MAP[name] || Security;
  return <Icon {...props} />;
};

// Compliance frameworks with industry associations and check counts
const COMPLIANCE_FRAMEWORKS = [
  {
    id: 'dpdp-2023',
    name: 'DPDP Act, 2023',
    description: 'Digital Personal Data Protection Act - India',
    icon: 'Shield',
    color: '#FF9933',
    industries: ['All Industries (India)', 'Technology', 'E-commerce', 'Financial Services'],
    checkCount: 14,
    categories: ['Data Localization', 'Consent Management', 'Data Principal Rights', 'Security Safeguards', 'Breach Notification'],
    severity: { high: 6, medium: 6, low: 2 },
    estimatedTime: '4-6 minutes',
    description_long: 'DPDP Act 2023 ensures protection of personal data of Indian citizens with requirements for data localization, consent management, and data principal rights.',
    official_source_url: 'https://www.meity.gov.in/writereaddata/files/Digital%20Personal%20Data%20Protection%20Act%202023.pdf'
  },
  {
    id: 'soc2-type1',
    name: 'SOC 2 Type I',
    description: 'Service Organization Control 2 - Design Effectiveness',
    icon: 'Security',
    color: '#1976d2',
    industries: ['Technology', 'SaaS', 'Cloud Services', 'Financial Services'],
    checkCount: 21,
    categories: ['Security', 'Availability', 'Processing Integrity', 'Confidentiality', 'Privacy'],
    severity: { high: 10, medium: 9, low: 2 },
    estimatedTime: '6-8 minutes',
    description_long: 'SOC 2 Type I evaluates the design of controls at a specific point in time across Trust Services Criteria.',
    official_source_url: 'https://www.aicpa.org/soc4so'
  },
  {
    id: 'soc2-type2',
    name: 'SOC 2 Type II',
    description: 'Service Organization Control 2 - Operating Effectiveness',
    icon: 'Security',
    color: '#1565c0',
    industries: ['Technology', 'SaaS', 'Cloud Services', 'Financial Services'],
    checkCount: 21,
    categories: ['Security', 'Availability', 'Processing Integrity', 'Confidentiality', 'Privacy'],
    severity: { high: 10, medium: 9, low: 2 },
    estimatedTime: '6-8 minutes',
    description_long: 'SOC 2 Type II evaluates the operating effectiveness of controls over a period of time (typically 6-12 months).',
    official_source_url: 'https://www.aicpa.org/soc4so'
  },
  {
    id: 'iso27701',
    name: 'ISO 27701',
    description: 'Privacy Information Management System',
    icon: 'Shield',
    color: '#9c27b0',
    industries: ['All Industries', 'Technology', 'Healthcare', 'Financial Services'],
    checkCount: 21,
    categories: ['PII Processing', 'Data Subject Rights', 'Privacy Controls', 'Privacy by Design', 'Third-party Processing'],
    severity: { high: 10, medium: 9, low: 2 },
    estimatedTime: '6-8 minutes',
    description_long: 'ISO 27701 extends ISO 27001 and ISO 27002 with requirements and guidance for establishing, implementing, maintaining and continually improving a Privacy Information Management System (PIMS).',
    official_source_url: 'https://www.iso.org/standard/71670.html'
  },
  {
    id: 'cis-controls',
    name: 'CIS Controls v8',
    description: 'Center for Internet Security Critical Security Controls',
    icon: 'Security',
    color: '#2e7d32',
    industries: ['All Industries', 'Critical Infrastructure', 'Government', 'Education'],
    checkCount: 7,
    categories: ['Asset Management', 'Data Protection', 'Secure Configuration', 'Access Control', 'Vulnerability Management'],
    severity: { high: 4, medium: 2, low: 1 },
    estimatedTime: '3-4 minutes',
    description_long: 'CIS Controls v8 provide 18 prioritized safeguards to mitigate the most prevalent cyber-attacks, organized into Implementation Groups (IG1, IG2, IG3).',
    official_source_url: 'https://www.cisecurity.org/controls/v8'
  },
  {
    id: 'cert-in',
    name: 'CERT-In Cyber Guidelines',
    description: 'Indian Computer Emergency Response Team Directives',
    icon: 'Security',
    color: '#FF6600',
    industries: ['All Industries (India)', 'Service Providers', 'Data Centers', 'Cloud Providers'],
    checkCount: 2,
    categories: ['Incident Reporting', 'Log Management', 'Time Synchronization', 'Vulnerability Management', 'KYC Requirements'],
    severity: { high: 1, medium: 1, low: 0 },
    estimatedTime: '2-3 minutes',
    description_long: 'CERT-In Guidelines mandate cybersecurity measures including 6-hour incident reporting, 180-day log retention, and vulnerability management for Indian organizations.',
    official_source_url: 'https://www.cert-in.org.in/'
  },
  {
    id: 'rbi-framework',
    name: 'RBI IT Security Framework',
    description: 'Reserve Bank of India IT and Cyber Security Framework',
    icon: 'AccountBalance',
    color: '#1a237e',
    industries: ['Banking', 'Financial Services', 'NBFCs', 'Payment Systems'],
    checkCount: 4,
    categories: ['IT Governance', 'Cyber Security', 'Access Control', 'Data Protection', 'Business Continuity', 'Incident Response'],
    severity: { high: 3, medium: 1, low: 0 },
    estimatedTime: '2-3 minutes',
    description_long: 'RBI IT Security Framework provides comprehensive guidelines for IT governance, cyber resilience, and security controls for Indian financial institutions.',
    official_source_url: 'https://www.rbi.org.in/Scripts/BS_ViewMasDirections.aspx'
  },
  {
    id: 'sox',
    name: 'SOX',
    description: 'Sarbanes-Oxley Act - IT Controls',
    icon: 'Gavel',
    color: '#b71c1c',
    industries: ['Public Companies', 'Financial Services', 'Accounting Firms'],
    checkCount: 7,
    categories: ['Access Control', 'Segregation of Duties', 'Audit Trails', 'Change Management', 'Data Integrity'],
    severity: { high: 4, medium: 2, low: 1 },
    estimatedTime: '3-4 minutes',
    description_long: 'SOX compliance ensures internal controls over financial reporting (ICFR) with focus on access controls, audit trails, and segregation of duties.',
    official_source_url: 'https://www.sec.gov/spotlight/sarbanes-oxley.htm'
  },
  {
    id: 'iso27017',
    name: 'ISO 27017',
    description: 'Cloud Security Controls',
    icon: 'Security',
    color: '#00838f',
    industries: ['Cloud Service Providers', 'Cloud Customers', 'SaaS', 'IaaS', 'PaaS'],
    checkCount: 3,
    categories: ['Shared Responsibility', 'Virtual Environment Security', 'Cloud Network', 'Data Segregation', 'Cloud IAM'],
    severity: { high: 2, medium: 1, low: 0 },
    estimatedTime: '2-3 minutes',
    description_long: 'ISO 27017 provides cloud-specific information security controls supplementing ISO 27002 for cloud service providers and customers.',
    official_source_url: 'https://www.iso.org/standard/43757.html'
  },
  {
    id: 'iso27018',
    name: 'ISO 27018',
    description: 'Cloud Privacy Protection',
    icon: 'Shield',
    color: '#4a148c',
    industries: ['Cloud Service Providers', 'Public Cloud', 'SaaS Processing PII'],
    checkCount: 2,
    categories: ['PII Processing', 'Consent Management', 'Data Subject Rights', 'PII Security', 'Sub-processor Management'],
    severity: { high: 1, medium: 1, low: 0 },
    estimatedTime: '2-3 minutes',
    description_long: 'ISO 27018 establishes commonly accepted control objectives, controls and guidelines for implementing measures to protect Personally Identifiable Information (PII) in public cloud computing.',
    official_source_url: 'https://www.iso.org/standard/76559.html'
  },
  {
    id: 'csa-star',
    name: 'CSA STAR',
    description: 'Cloud Security Alliance Security, Trust, Assurance and Risk',
    icon: 'Security',
    color: '#01579b',
    industries: ['Cloud Service Providers', 'Cloud Security', 'SaaS', 'IaaS', 'PaaS'],
    checkCount: 1,
    categories: ['Application Security', 'Data Security', 'Encryption', 'IAM', 'Infrastructure Security', 'Threat Management'],
    severity: { high: 1, medium: 0, low: 0 },
    estimatedTime: '1-2 minutes',
    description_long: 'CSA STAR provides a comprehensive cloud security assessment based on the Cloud Controls Matrix (CCM) covering 16 domains of cloud security.',
    official_source_url: 'https://cloudsecurityalliance.org/star/'
  },
  {
    id: 'owasp-top10',
    name: 'OWASP Top 10',
    description: 'Web Application Security Risks',
    icon: 'Security',
    color: '#e65100',
    industries: ['Web Applications', 'APIs', 'Cloud Services', 'SaaS'],
    checkCount: 2,
    categories: ['Access Control', 'Cryptographic Failures', 'Injection', 'Security Misconfiguration', 'Authentication'],
    severity: { high: 1, medium: 1, low: 0 },
    estimatedTime: '2-3 minutes',
    description_long: 'OWASP Top 10 represents the most critical security risks to web applications, providing awareness and guidance for secure development.',
    official_source_url: 'https://owasp.org/www-project-top-ten/'
  },
  {
    id: 'iso22301',
    name: 'ISO 22301',
    description: 'Business Continuity Management System',
    icon: 'Assessment',
    color: '#33691e',
    industries: ['All Industries', 'Critical Infrastructure', 'Financial Services', 'Healthcare'],
    checkCount: 3,
    categories: ['Business Impact Analysis', 'Risk Assessment', 'BC Strategy', 'BC Plans', 'BC Testing', 'Recovery Objectives'],
    severity: { high: 2, medium: 1, low: 0 },
    estimatedTime: '2-3 minutes',
    description_long: 'ISO 22301 specifies requirements for a business continuity management system (BCMS) to protect against, reduce the likelihood of, and ensure recovery from disruptive incidents.',
    official_source_url: 'https://www.iso.org/standard/75106.html'
  },
  // Legacy frameworks (keeping for backward compatibility)
  {
    id: 'pci-dss',
    name: 'PCI DSS',
    description: 'Payment Card Industry Data Security Standard',
    icon: 'AccountBalance',
    color: '#d32f2f',
    industries: ['E-commerce', 'Retail', 'Financial Services', 'Payment Processing'],
    checkCount: 18,
    categories: ['Network Security', 'Data Protection', 'Access Control', 'Monitoring', 'Security Policies'],
    severity: { high: 9, medium: 7, low: 2 },
    estimatedTime: '5-7 minutes',
    description_long: 'PCI DSS ensures secure handling of credit card information through comprehensive security requirements for organizations that process, store, or transmit cardholder data.',
    official_source_url: 'https://www.pcisecuritystandards.org/document_library/'
  },
  {
    id: 'hipaa',
    name: 'HIPAA',
    description: 'Health Insurance Portability and Accountability Act',
    icon: 'LocalHospital',
    color: '#388e3c',
    industries: ['Healthcare', 'Medical Devices', 'Health Insurance', 'Pharmaceuticals'],
    checkCount: 10,
    categories: ['Administrative Safeguards', 'Physical Safeguards', 'Technical Safeguards', 'Breach Notification'],
    severity: { high: 5, medium: 4, low: 1 },
    estimatedTime: '4-5 minutes',
    description_long: 'HIPAA compliance ensures the protection of sensitive patient health information through administrative, physical, and technical safeguards.',
    official_source_url: 'https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html'
  },
  {
    id: 'gdpr',
    name: 'GDPR',
    description: 'General Data Protection Regulation',
    icon: 'Shield',
    color: '#7b1fa2',
    industries: ['All Industries (EU Operations)', 'Technology', 'Marketing', 'E-commerce'],
    checkCount: 7,
    categories: ['Data Protection', 'Privacy by Design', 'Consent Management', 'Data Subject Rights', 'Breach Notification'],
    severity: { high: 4, medium: 2, low: 1 },
    estimatedTime: '3-4 minutes',
    estimatedTime: '10-15 minutes',
    description_long: 'GDPR compliance ensures protection of personal data and privacy rights for individuals within the European Union and European Economic Area.',
    official_source_url: 'https://gdpr.eu/'
  },
  {
    id: 'iso27001',
    name: 'ISO 27001',
    description: 'Information Security Management System',
    icon: 'Gavel',
    color: '#f57c00',
    industries: ['All Industries', 'Government', 'Financial Services', 'Technology'],
    checkCount: 6,
    categories: ['Information Security Policy', 'Risk Management', 'Asset Management', 'Access Control', 'Incident Management'],
    severity: { high: 3, medium: 2, low: 1 },
    estimatedTime: '3-4 minutes',
    description_long: 'ISO 27001 provides a systematic approach to managing sensitive company information through an Information Security Management System (ISMS).',
    official_source_url: 'https://www.iso.org/standard/27001'
  },
  {
    id: 'nist-csf',
    name: 'NIST Cybersecurity Framework',
    description: 'National Institute of Standards and Technology Framework',
    icon: 'Assessment',
    color: '#5d4037',
    industries: ['Government', 'Critical Infrastructure', 'Financial Services', 'Healthcare'],
    checkCount: 4,
    categories: ['Identify', 'Protect', 'Detect', 'Respond', 'Recover'],
    severity: { high: 2, medium: 1, low: 1 },
    estimatedTime: '2-3 minutes',
    description_long: 'NIST Cybersecurity Framework provides a policy framework of computer security guidance for organizations to assess and improve their cybersecurity posture.',
    official_source_url: 'https://www.nist.gov/cyberframework'
  },
  {
    id: 'fedramp',
    name: 'FedRAMP',
    description: 'Federal Risk and Authorization Management Program',
    icon: 'Business',
    color: '#1565c0',
    industries: ['Government Contractors', 'Cloud Service Providers', 'Federal Agencies'],
    checkCount: 5,
    categories: ['Access Control', 'Audit and Accountability', 'Configuration Management', 'Incident Response', 'Risk Assessment'],
    severity: { high: 3, medium: 1, low: 1 },
    estimatedTime: '3-4 minutes',
    description_long: 'FedRAMP provides a standardized approach to security assessment, authorization, and continuous monitoring for cloud products and services used by federal agencies.',
    official_source_url: 'https://www.fedramp.gov/'
  }
];

function Compliance() {
  const [selectedCompliance, setSelectedCompliance] = useState('');
  const [scanResults, setScanResults] = useState(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [scanHistory, setScanHistory] = useState([]);
  const [lastScanTimestamp, setLastScanTimestamp] = useState(null);
  const [showScanHistory, setShowScanHistory] = useState(false);
  const [expandedFramework, setExpandedFramework] = useState('');
  const [frameworks, setFrameworks] = useState([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({});
  const { addNotification } = useNotifications();
  const [selectedIndustry, setSelectedIndustry] = useState('All');

  // Industry categories for filtering
  const INDUSTRY_CATEGORIES = [
    { id: 'All', name: 'All Frameworks', icon: <Security /> },
    { id: 'India', name: 'India-Specific', icon: <Shield />, color: '#FF9933' },
    { id: 'Cloud', name: 'Cloud Security', icon: <Security />, color: '#01579b' },
    { id: 'Privacy', name: 'Privacy & Data Protection', icon: <Shield />, color: '#9c27b0' },
    { id: 'Financial', name: 'Financial & Audit', icon: <AccountBalance />, color: '#1a237e' },
    { id: 'Healthcare', name: 'Healthcare', icon: <LocalHospital />, color: '#388e3c' },
    { id: 'Government', name: 'Government', icon: <Business />, color: '#5d4037' }
  ];

  // Map frameworks to industries
  const getFrameworkIndustry = (frameworkId) => {
    const industryMap = {
      'dpdp-2023': 'India',
      'cert-in': 'India',
      'rbi-framework': 'India',
      'iso27017': 'Cloud',
      'iso27018': 'Cloud',
      'csa-star': 'Cloud',
      'gdpr': 'Privacy',
      'iso27701': 'Privacy',
      'sox': 'Financial',
      'soc2-type1': 'Financial',
      'soc2-type2': 'Financial',
      'soc2': 'Financial',
      'pci-dss': 'Financial',
      'hipaa': 'Healthcare',
      'fedramp': 'Government',
      'nist-csf': 'Government',
      'iso27001': 'All',
      'cis-controls': 'All',
      'owasp-top10': 'All',
      'iso22301': 'All'
    };
    return industryMap[frameworkId] || 'All';
  };

  // Filter frameworks by industry
  const filteredFrameworks = selectedIndustry === 'All' 
    ? frameworks 
    : frameworks.filter(f => getFrameworkIndustry(f.framework_id) === selectedIndustry);

  useEffect(() => {
    loadAWSAccounts();
    loadScanHistoryFromStorage(); // Load scan history from localStorage
    loadComplianceFrameworks();
  }, []);

  useEffect(() => {
    if (selectedCompliance) {
      loadComplianceChecks(selectedCompliance);
      // Load last scan if available
      if (selectedAccount) {
        loadLastScan(selectedCompliance, selectedAccount);
      }
    }
  }, [selectedCompliance, selectedAccount]);

  const loadLastScan = async (framework, accountId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/compliance/scan/latest/${framework}/${accountId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.hasScan) {
          setScanResults(data);
          console.log('Loaded previous scan:', data.scanId);
          
          addNotification({
            type: 'info',
            title: 'Previous Scan Loaded',
            message: `Loaded scan from ${new Date(data.scanDate).toLocaleString()}`
          });
        }
      }
    } catch (err) {
      console.error('Failed to load last scan:', err);
      // Don't show error notification - it's okay if there's no previous scan
    }
  };

  const loadComplianceFrameworks = async () => {
    try {
      console.log('Loading compliance frameworks...');
      const token = localStorage.getItem('token');
      console.log('Token exists:', !!token);
      
      const response = await fetch('/api/compliance/frameworks', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('Frameworks data received:', data);
        if (data.frameworks && data.frameworks.length > 0) {
          setFrameworks(data.frameworks);
          console.log('Frameworks set from API:', data.frameworks?.length || 0);
        } else {
          console.log('No frameworks from API, using fallback');
          // Fallback to hardcoded frameworks if API returns empty
          const fallbackFrameworks = COMPLIANCE_FRAMEWORKS.map(f => ({
            framework_id: f.id,
            name: f.name,
            description: f.description,
            official_source_url: f.official_source_url,
            downloaded_checks: 0,
            download_status: null,
            downloaded_version: null,
            download_date: null
          }));
          setFrameworks(fallbackFrameworks);
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to load frameworks - Response not ok:', response.status, errorText);
        // Fallback to hardcoded frameworks on API error
        console.log('Using fallback frameworks due to API error');
        const fallbackFrameworks = COMPLIANCE_FRAMEWORKS.map(f => ({
          framework_id: f.id,
          name: f.name,
          description: f.description,
          official_source_url: f.official_source_url,
          downloaded_checks: 0,
          download_status: null,
          downloaded_version: null,
          download_date: null
        }));
        setFrameworks(fallbackFrameworks);
      }
    } catch (err) {
      console.error('Failed to load compliance frameworks - Exception:', err);
      // Fallback to hardcoded frameworks on exception
      console.log('Using fallback frameworks due to exception');
      const fallbackFrameworks = COMPLIANCE_FRAMEWORKS.map(f => ({
        framework_id: f.id,
        name: f.name,
        description: f.description,
        official_source_url: f.official_source_url,
        downloaded_checks: 0,
        download_status: null,
        downloaded_version: null,
        download_date: null
      }));
      setFrameworks(fallbackFrameworks);
    }
  };

  const loadAWSAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/aws/accounts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAwsAccounts(data.accounts);
        if (data.accounts.length > 0) {
          setSelectedAccount(data.accounts[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load AWS accounts:', err);
    }
  };

  const downloadComplianceChecks = async (frameworkId) => {
    setIsDownloading(true);
    setDownloadProgress(prev => ({ ...prev, [frameworkId]: 0 }));

    try {
      const framework = frameworks.find(f => f.framework_id === frameworkId);
      
      // Debug: Check if addNotification is a function
      console.log('addNotification type:', typeof addNotification);
      console.log('addNotification:', addNotification);
      
      try {
        addNotification({
          type: 'info',
          title: 'Downloading Compliance Checks',
          message: `Downloading official ${framework?.name} compliance checks from government sources...`
        });
      } catch (notifError) {
        console.error('Notification error:', notifError);
        console.error('Error stack:', notifError.stack);
      }

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setDownloadProgress(prev => ({
          ...prev,
          [frameworkId]: Math.min((prev[frameworkId] || 0) + Math.random() * 15, 95)
        }));
      }, 500);

      const token = localStorage.getItem('token');
      const response = await fetch(`/api/compliance/download/${frameworkId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      clearInterval(progressInterval);
      setDownloadProgress(prev => ({ ...prev, [frameworkId]: 100 }));

      if (response.ok) {
        const data = await response.json();
        
        try {
          addNotification({
            type: 'success',
            title: 'Download Completed',
            message: `Successfully downloaded ${data.checksCount} official compliance checks for ${framework?.name} v${data.version}`
          });
        } catch (notifError) {
          console.error('Success notification error:', notifError);
        }
        
        // Refresh frameworks to show updated status
        loadComplianceFrameworks();
        
        // If this framework is selected, reload its checks
        if (selectedCompliance === frameworkId) {
          loadComplianceChecks(frameworkId);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.details || 'Download failed');
      }
    } catch (err) {
      console.error('Compliance check download failed:', err);
      console.error('Error type:', err.constructor.name);
      console.error('Error stack:', err.stack);
      
      try {
        addNotification({
          type: 'error',
          title: 'Download Failed',
          message: `Failed to download compliance checks: ${err.message}`
        });
      } catch (notifError) {
        console.error('Error notification failed:', notifError);
        // Fallback: show alert if notification fails
        alert(`Download failed: ${err.message}`);
      }
    } finally {
      setIsDownloading(false);
      setDownloadProgress(prev => ({ ...prev, [frameworkId]: 0 }));
    }
  };
  const loadComplianceChecks = async (frameworkId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/compliance/checks/${frameworkId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`Loaded ${data.checks.length} checks for ${frameworkId}`);
        
        if (data.checks.length === 0) {
          addNotification({
            type: 'warning',
            title: 'No Compliance Checks',
            message: `No compliance checks found for ${data.frameworkName}. Please download official checks first.`
          });
        } else {
          addNotification({
            type: 'info',
            title: 'Compliance Checks Loaded',
            message: `Loaded ${data.checks.length} security checks for ${data.frameworkName} v${data.version || 'latest'}`
          });
        }
      }
    } catch (err) {
      console.error('Failed to load compliance checks:', err);
    }
  };

  const loadScanHistoryFromStorage = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/compliance/history', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.scans) {
          // Transform API response to match frontend format
          const transformedScans = data.scans.map(scan => ({
            id: scan.scanId,
            scanId: scan.scanId,
            timestamp: scan.scanDate,
            accountId: scan.accountId,
            accountName: scan.accountName,
            frameworkId: scan.frameworkId,
            frameworkName: scan.frameworkName,
            summary: scan.summary,
            results: {
              scanId: scan.scanId,
              framework: scan.frameworkId,
              frameworkName: scan.frameworkName,
              accountId: scan.accountId,
              scanDate: scan.scanDate,
              status: scan.status,
              summary: scan.summary,
              categories: scan.categories,
              recommendations: scan.recommendations
            }
          }));
          
          setScanHistory(transformedScans);
          
          // Set last scan timestamp if available
          if (transformedScans.length > 0) {
            setLastScanTimestamp(transformedScans[0].timestamp);
          }
        }
      }
    } catch (error) {
      console.error('Error loading scan history:', error);
    }
  };

  const saveScanToHistory = async (results, accountId, frameworkId) => {
    // Scan is already saved to database by the backend
    // Just reload the history to update the UI
    await loadScanHistoryFromStorage();
  };

  const loadScanFromHistory = (scanEntry) => {
    setScanResults(scanEntry.results);
    setLastScanTimestamp(scanEntry.timestamp);
    setSelectedAccount(scanEntry.accountId);
    setSelectedCompliance(scanEntry.frameworkId);
    setShowScanHistory(false);
    
    addNotification({
      type: 'info',
      title: 'Historical Scan Loaded',
      message: `Viewing ${scanEntry.frameworkName} scan from ${formatTimestamp(scanEntry.timestamp)}`
    });
  };

  const clearScanHistory = async () => {
    if (window.confirm('Are you sure you want to clear all scan history? This cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch('/api/compliance/history', {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setScanHistory([]);
          setLastScanTimestamp(null);
          addNotification({
            type: 'success',
            title: 'History Cleared',
            message: data.message || 'All scan history has been cleared'
          });
        } else {
          throw new Error('Failed to clear history');
        }
      } catch (error) {
        console.error('Error clearing scan history:', error);
        addNotification({
          type: 'error',
          title: 'Clear Failed',
          message: 'Failed to clear scan history'
        });
      }
    }
  };

  const deleteScanFromHistory = async (scanId, event) => {
    event.stopPropagation(); // Prevent loading the scan when clicking delete
    
    if (window.confirm('Are you sure you want to delete this scan?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/compliance/history/${scanId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          // Reload history to update the UI
          await loadScanHistoryFromStorage();
          addNotification({
            type: 'success',
            title: 'Scan Deleted',
            message: 'Scan has been removed from history'
          });
        } else {
          throw new Error('Failed to delete scan');
        }
      } catch (error) {
        console.error('Error deleting scan:', error);
        addNotification({
          type: 'error',
          title: 'Delete Failed',
          message: 'Failed to delete scan from history'
        });
      }
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFullTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const downloadHistoricalCompliancePDF = (scan) => {
    try {
      const htmlContent = generateComplianceScanHTML(scan);
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      setTimeout(() => {
        printWindow.print();
      }, 500);

      addNotification({
        type: 'success',
        title: 'Report Ready',
        message: `${scan.frameworkName} report from ${formatTimestamp(scan.timestamp)} opened. Use browser's Print to PDF.`
      });
    } catch (error) {
      console.error('PDF generation failed:', error);
      addNotification({
        type: 'error',
        title: 'Download Failed',
        message: 'Failed to generate PDF report'
      });
    }
  };

  const downloadHistoricalComplianceCSV = (scan) => {
    try {
      const csvContent = generateComplianceScanCSV(scan);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      const filename = `compliance-${scan.frameworkId}-${scan.accountName.replace(/\s+/g, '-')}-${new Date(scan.timestamp).toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addNotification({
        type: 'success',
        title: 'CSV Downloaded',
        message: `${scan.frameworkName} report from ${formatTimestamp(scan.timestamp)} downloaded successfully`
      });
    } catch (error) {
      console.error('CSV generation failed:', error);
      addNotification({
        type: 'error',
        title: 'Download Failed',
        message: 'Failed to generate CSV report'
      });
    }
  };

  const generateComplianceScanHTML = (scan) => {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Regulatory Compliance Report - ${scan.frameworkName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #e91e63; }
          h2 { color: #424242; margin-top: 30px; }
          .summary { background-color: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .summary-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-top: 15px; }
          .summary-card { background: white; padding: 15px; border-radius: 4px; text-align: center; }
          .summary-value { font-size: 32px; font-weight: bold; margin: 10px 0; }
          .score { color: ${scan.summary.complianceScore >= 80 ? '#4caf50' : scan.summary.complianceScore >= 60 ? '#ff9800' : '#f44336'}; }
          .passed { color: #4caf50; }
          .failed { color: #f44336; }
          .warnings { color: #ff9800; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          th { background-color: #e91e63; color: white; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <h1>Regulatory Compliance Report</h1>
        <div class="summary">
          <p><strong>Framework:</strong> ${scan.frameworkName}</p>
          <p><strong>Account:</strong> ${scan.accountName}</p>
          <p><strong>Scan Date:</strong> ${formatFullTimestamp(scan.timestamp)}</p>
          <p><strong>Scan ID:</strong> ${scan.id}</p>
          
          <div class="summary-grid">
            <div class="summary-card">
              <div>Compliance Score</div>
              <div class="summary-value score">${scan.summary.complianceScore}%</div>
            </div>
            <div class="summary-card">
              <div>Passed</div>
              <div class="summary-value passed">${scan.summary.passed}</div>
            </div>
            <div class="summary-card">
              <div>Failed</div>
              <div class="summary-value failed">${scan.summary.failed}</div>
            </div>
            <div class="summary-card">
              <div>Warnings</div>
              <div class="summary-value warnings">${scan.summary.warnings || 0}</div>
            </div>
          </div>
        </div>
        
        <h2>Summary</h2>
        <table>
          <tr>
            <th>Total Checks</th>
            <th>Passed</th>
            <th>Failed</th>
            <th>Warnings</th>
            <th>Compliance Score</th>
          </tr>
          <tr>
            <td>${scan.summary.total || 0}</td>
            <td class="passed">${scan.summary.passed || 0}</td>
            <td class="failed">${scan.summary.failed || 0}</td>
            <td class="warnings">${scan.summary.warnings || 0}</td>
            <td class="score">${scan.summary.complianceScore}%</td>
          </tr>
        </table>
        
        <p class="no-print" style="margin-top: 40px; text-align: center;">
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">
            Print / Save as PDF
          </button>
        </p>
      </body>
      </html>
    `;
  };

  const generateComplianceScanCSV = (scan) => {
    let csv = `Regulatory Compliance Report - ${scan.frameworkName}\n`;
    csv += `Account: ${scan.accountName}\n`;
    csv += `Scan Date: ${formatFullTimestamp(scan.timestamp)}\n`;
    csv += `Scan ID: ${scan.id}\n`;
    csv += `\n`;
    csv += `Summary:\n`;
    csv += `Compliance Score,Total Checks,Passed,Failed,Warnings\n`;
    csv += `${scan.summary.complianceScore}%,${scan.summary.total || 0},${scan.summary.passed || 0},${scan.summary.failed || 0},${scan.summary.warnings || 0}\n`;
    csv += `\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n`;
    
    return csv;
  };

  const startComplianceScan = async () => {
    if (!selectedCompliance || !selectedAccount) {
      addNotification({
        type: 'error',
        title: 'Selection Required',
        message: 'Please select both a compliance framework and AWS account.'
      });
      return;
    }

    setIsScanning(true);
    setScanProgress(0);
    setScanResults(null);

    try {
      const framework = frameworks.find(f => f.framework_id === selectedCompliance);
      
      addNotification({
        type: 'info',
        title: 'Regulatory Compliance Scan Started',
        message: `Starting ${framework?.name} compliance scan using AWS APIs with ${framework?.downloaded_checks} official checks.`
      });

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setScanProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          return prev + Math.random() * 8;
        });
      }, 2000);

      const token = localStorage.getItem('token');
      const response = await fetch('/api/compliance/scan', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          accountId: selectedAccount,
          framework: selectedCompliance,
          useAwsApis: true // Flag to use real AWS APIs
        })
      });

      clearInterval(progressInterval);
      setScanProgress(100);

      if (response.ok) {
        const data = await response.json();
        const timestamp = new Date().toISOString();
        
        setScanResults(data);
        setLastScanTimestamp(timestamp);
        
        // Save scan to history
        saveScanToHistory(data, selectedAccount, selectedCompliance);
        
        addNotification({
          type: 'success',
          title: 'Regulatory Compliance Scan Completed',
          message: `${framework?.name} scan completed. Found ${data.summary.failed} issues across ${data.summary.total} checks.`
        });
      } else {
        const errorData = await response.json();
        
        // Log detailed error to console
        console.error('Compliance scan failed:', {
          status: response.status,
          error: errorData.error,
          details: errorData.details,
          troubleshooting: errorData.troubleshooting,
          framework: selectedCompliance,
          account: selectedAccount
        });
        
        // Show detailed error notification
        addNotification({
          type: 'error',
          title: 'AWS API Access Failed',
          message: `${errorData.error}: ${errorData.details || 'Please check AWS credentials and permissions.'}`
        });
        
        // Show troubleshooting steps in console
        if (errorData.troubleshooting) {
          console.group('🔧 Troubleshooting Steps:');
          errorData.troubleshooting.forEach((step, index) => {
            console.log(`${index + 1}. ${step}`);
          });
          console.groupEnd();
        }
        
        throw new Error(errorData.error || 'Compliance scan failed');
      }
    } catch (err) {
      console.error('Compliance scan failed:', {
        error: err.message,
        stack: err.stack,
        framework: selectedCompliance,
        account: selectedAccount,
        timestamp: new Date().toISOString()
      });
      
      const framework = frameworks.find(f => f.framework_id === selectedCompliance);
      
      addNotification({
        type: 'error',
        title: 'Regulatory Compliance Scan Failed',
        message: `Failed to complete ${framework?.name || 'compliance'} scan. ${err.message}`
      });
      
      // Clear any partial results
      setScanResults(null);
    } finally {
      setIsScanning(false);
      setScanProgress(0);
    }
  };

  const downloadReport = async (format) => {
    if (!scanResults) return;

    try {
      addNotification({
        type: 'info',
        title: 'Generating Report',
        message: `Preparing ${format.toUpperCase()} report...`
      });

      if (format === 'csv') {
        // Generate CSV
        const csvContent = generateCSVReport(scanResults);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `compliance-report-${scanResults.scanId}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        addNotification({
          type: 'success',
          title: 'Report Downloaded',
          message: `CSV report downloaded successfully`
        });
      } else if (format === 'pdf') {
        // For PDF, we'll generate HTML and let browser print to PDF
        const htmlContent = generateHTMLReport(scanResults);
        const printWindow = window.open('', '_blank');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        
        setTimeout(() => {
          printWindow.print();
        }, 500);

        addNotification({
          type: 'success',
          title: 'Report Ready',
          message: `PDF report opened in new window. Use browser's Print to PDF feature.`
        });
      }
    } catch (error) {
      console.error('Report generation failed:', error);
      addNotification({
        type: 'error',
        title: 'Report Generation Failed',
        message: error.message
      });
    }
  };

  const generateCSVReport = (results) => {
    const framework = frameworks.find(f => f.framework_id === selectedCompliance);
    let csv = `Compliance Report - ${framework?.name || results.frameworkName}\n`;
    csv += `Scan ID: ${results.scanId}\n`;
    csv += `Scan Date: ${new Date(results.scanDate).toLocaleString()}\n`;
    csv += `Account: ${results.accountName || results.accountId}\n`;
    csv += `Compliance Score: ${results.summary.complianceScore}%\n`;
    csv += `\n`;
    csv += `Summary:\n`;
    csv += `Total Checks,Passed,Failed,Warnings,Errors\n`;
    csv += `${results.summary.total},${results.summary.passed},${results.summary.failed},${results.summary.warnings || 0},${results.summary.errors || 0}\n`;
    csv += `\n`;
    csv += `Detailed Findings:\n`;
    csv += `Category,Check ID,Title,Resource ID,Resource Type,Region,Status,Severity,Remediation Complexity,Estimated Time\n`;
    
    results.categories.forEach(category => {
      category.findings.forEach(finding => {
        const row = [
          category.name,
          finding.checkId || '',
          `"${finding.title}"`,
          finding.resourceId || finding.resource || 'N/A',
          finding.resourceType || '',
          finding.resourceRegion || '',
          finding.status,
          finding.severity || '',
          finding.remediationComplexity || '',
          finding.estimatedRemediationTime || ''
        ].join(',');
        csv += row + '\n';
      });
    });
    
    return csv;
  };

  const generateHTMLReport = (results) => {
    const framework = frameworks.find(f => f.framework_id === selectedCompliance);
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Compliance Report - ${framework?.name || results.frameworkName}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #1976d2; }
          h2 { color: #424242; margin-top: 30px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
          th { background-color: #1976d2; color: white; }
          .summary { background-color: #f5f5f5; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .pass { color: #4caf50; font-weight: bold; }
          .fail { color: #f44336; font-weight: bold; }
          .high { background-color: #ffebee; }
          .medium { background-color: #fff3e0; }
          .low { background-color: #e3f2fd; }
          @media print { .no-print { display: none; } }
        </style>
      </head>
      <body>
        <h1>Compliance Report: ${framework?.name || results.frameworkName}</h1>
        <div class="summary">
          <p><strong>Scan ID:</strong> ${results.scanId}</p>
          <p><strong>Scan Date:</strong> ${new Date(results.scanDate).toLocaleString()}</p>
          <p><strong>Account:</strong> ${results.accountName || results.accountId}</p>
          <p><strong>Compliance Score:</strong> ${results.summary.complianceScore}%</p>
        </div>
        
        <h2>Summary</h2>
        <table>
          <tr>
            <th>Total Checks</th>
            <th>Passed</th>
            <th>Failed</th>
            <th>Warnings</th>
          </tr>
          <tr>
            <td>${results.summary.total}</td>
            <td class="pass">${results.summary.passed}</td>
            <td class="fail">${results.summary.failed}</td>
            <td>${results.summary.warnings || 0}</td>
          </tr>
        </table>
        
        <h2>Detailed Findings</h2>
        ${results.categories.map(category => `
          <h3>${category.name}</h3>
          <table>
            <tr>
              <th>Check</th>
              <th>Resource</th>
              <th>Status</th>
              <th>Severity</th>
            </tr>
            ${category.findings.map(finding => `
              <tr class="${finding.severity?.toLowerCase()}">
                <td>${finding.title}</td>
                <td>${finding.resourceId || finding.resource || 'N/A'}</td>
                <td class="${finding.status === 'PASS' ? 'pass' : 'fail'}">${finding.status}</td>
                <td>${finding.severity || ''}</td>
              </tr>
            `).join('')}
          </table>
        `).join('')}
        
        <p class="no-print" style="margin-top: 40px; text-align: center;">
          <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer;">
            Print / Save as PDF
          </button>
        </p>
      </body>
      </html>
    `;
  };

  const renderComplianceFrameworkCard = (framework) => {
    const isDownloaded = framework.downloaded_checks > 0;
    const isDownloading = downloadProgress[framework.framework_id] > 0;
    const progress = downloadProgress[framework.framework_id] || 0;
    
    return (
      <Card 
        key={framework.framework_id}
        sx={{ 
          cursor: 'pointer',
          borderRadius: 3,
          border: selectedCompliance === framework.framework_id ? 2 : 1,
          borderColor: selectedCompliance === framework.framework_id ? 'primary.main' : 'divider',
          bgcolor: selectedCompliance === framework.framework_id ? 'primary.50' : 'background.paper',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
            borderColor: 'primary.main'
          }
        }}
        onClick={() => setSelectedCompliance(framework.framework_id)}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
            <Avatar 
              sx={{ 
                width: 48, 
                height: 48, 
                bgcolor: framework.color || (isDownloaded ? 'success.main' : 'grey.400'),
                color: 'white'
              }}
            >
              {framework.icon ? <FrameworkIcon name={framework.icon} /> : <Security />}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {framework.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {framework.description}
              </Typography>
              
              {/* Download Status */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {isDownloaded ? (
                  <Chip 
                    label={`v${framework.downloaded_version} • ${framework.downloaded_checks} checks`}
                    size="small" 
                    color="success"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                ) : (
                  <Chip 
                    label="Not Downloaded"
                    size="small" 
                    color="warning"
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                )}
                
                {framework.download_date && (
                  <Typography variant="caption" color="text.secondary">
                    Downloaded: {new Date(framework.download_date).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          {/* Download Progress */}
          {isDownloading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Downloading from official sources... {Math.round(progress)}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ borderRadius: 1, height: 6 }}
              />
            </Box>
          )}
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              size="small"
              variant={isDownloaded ? "outlined" : "contained"}
              color="primary"
              startIcon={isDownloading ? <CircularProgress size={16} /> : <CloudDownload />}
              onClick={(e) => {
                e.stopPropagation();
                downloadComplianceChecks(framework.framework_id);
              }}
              disabled={isDownloading}
              sx={{ fontSize: '0.75rem' }}
            >
              {isDownloading ? 'Downloading...' : isDownloaded ? 'Update Checks' : 'Download Official Checks'}
            </Button>
            
            {isDownloaded && (
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(framework.official_source_url, '_blank');
                }}
                sx={{ fontSize: '0.75rem' }}
              >
                View Source
              </Button>
            )}
          </Box>

          {/* Framework Details */}
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpandedFramework(expandedFramework === framework.framework_id ? '' : framework.framework_id);
            }}
            sx={{ mt: 1, fontSize: '0.75rem' }}
          >
            {expandedFramework === framework.framework_id ? 'Show Less' : 'Show Details'}
          </Button>

          {expandedFramework === framework.framework_id && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {framework.description}
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Official Source:
              </Typography>
              <Typography variant="body2" color="primary.main" sx={{ mb: 2 }}>
                {framework.official_source_url}
              </Typography>
              
              {isDownloaded && (
                <>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Download Information:
                  </Typography>
                  <Typography variant="body2">
                    Version: {framework.downloaded_version}<br/>
                    Checks: {framework.downloaded_checks}<br/>
                    Status: {framework.download_status}<br/>
                    Downloaded: {new Date(framework.download_date).toLocaleString()}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderScanResults = () => {
    if (!scanResults) return null;

    const framework = COMPLIANCE_FRAMEWORKS.find(f => f.id === selectedCompliance);
    const { passed, failed, warnings, total } = scanResults.summary;
    const complianceScore = Math.round((passed / total) * 100);

    return (
      <Card sx={{ mt: 4, borderRadius: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Assessment color="primary" />
                {framework?.name || 'Regulatory Compliance'} Results
              </Typography>
              {lastScanTimestamp && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccessTime fontSize="small" />
                  Scanned {formatTimestamp(lastScanTimestamp)} ({formatFullTimestamp(lastScanTimestamp)})
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Download />}
                onClick={() => downloadReport('pdf')}
              >
                PDF Report
              </Button>
              <Button
                variant="outlined"
                startIcon={<Description />}
                onClick={() => downloadReport('csv')}
              >
                CSV Export
              </Button>
            </Box>
          </Box>

          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'success.50', border: '1px solid', borderColor: 'success.light' }}>
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {complianceScore}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Compliance Score
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'success.50' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {passed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Passed Checks
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'error.50' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'error.main' }}>
                  {failed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Failed Checks
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={3}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'warning.50' }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'warning.main' }}>
                  {warnings}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Warnings
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Detailed Findings by Category
          </Typography>
          
          {scanResults.categories.map((category, index) => (
            <Accordion key={index} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {category.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, ml: 'auto', mr: 2 }}>
                    <Chip 
                      label={`${category.passed} Passed`} 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                    <Chip 
                      label={`${category.failed} Failed`} 
                      size="small" 
                      color="error" 
                      variant="outlined"
                    />
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {/* Group findings by check title */}
                  {Object.entries(
                    category.findings.reduce((acc, finding) => {
                      const key = finding.checkId || finding.title;
                      if (!acc[key]) {
                        acc[key] = {
                          ...finding,
                          resources: []
                        };
                      }
                      acc[key].resources.push({
                        resourceId: finding.resourceId,
                        resourceType: finding.resourceType,
                        resourceArn: finding.resourceArn,
                        resourceRegion: finding.resourceRegion,
                        status: finding.status,
                        message: finding.message
                      });
                      return acc;
                    }, {})
                  ).map(([checkKey, groupedFinding], findingIndex) => (
                    <Box key={findingIndex} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                        <Box sx={{ mt: 0.5 }}>
                          {groupedFinding.status === 'PASS' ? (
                            <CheckCircle color="success" />
                          ) : groupedFinding.status === 'FAIL' ? (
                            <Error color="error" />
                          ) : (
                            <Warning color="warning" />
                          )}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {groupedFinding.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {groupedFinding.description}
                          </Typography>
                          
                          {/* Check Details */}
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            <Chip 
                              label={groupedFinding.severity || 'MEDIUM'} 
                              size="small" 
                              sx={{
                                bgcolor: 
                                  groupedFinding.severity === 'CRITICAL' ? '#d32f2f' :
                                  groupedFinding.severity === 'HIGH' ? '#f57c00' :
                                  groupedFinding.severity === 'MEDIUM' ? '#fbc02d' :
                                  groupedFinding.severity === 'LOW' ? '#1976d2' : '#757575',
                                color: '#fff',
                                fontWeight: 600
                              }}
                            />
                            <Chip 
                              label={`${groupedFinding.resources.length} Resource${groupedFinding.resources.length > 1 ? 's' : ''}`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                            <Chip 
                              label={`${groupedFinding.resources.filter(r => r.status === 'PASS').length} Passed`}
                              size="small"
                              variant="outlined"
                              color="success"
                            />
                            <Chip 
                              label={`${groupedFinding.resources.filter(r => r.status === 'FAIL').length} Failed`}
                              size="small"
                              variant="outlined"
                              color="error"
                            />
                          </Box>

                          {/* Affected Resources */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
                              Affected Resources:
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {groupedFinding.resources.map((resource, resourceIndex) => (
                                <Box 
                                  key={resourceIndex} 
                                  sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 1, 
                                    p: 1, 
                                    bgcolor: resource.status === 'PASS' ? '#e8f5e9' : '#ffebee',
                                    borderRadius: 1,
                                    border: 1,
                                    borderColor: resource.status === 'PASS' ? '#4caf50' : '#f44336'
                                  }}
                                >
                                  {resource.status === 'PASS' ? (
                                    <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />
                                  ) : (
                                    <Error sx={{ fontSize: 16, color: '#f44336' }} />
                                  )}
                                  <Chip 
                                    label={resource.resourceId || 'N/A'}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                      if (resource.resourceId && resource.resourceId !== 'N/A') {
                                        navigator.clipboard.writeText(resource.resourceId);
                                        addNotification({
                                          type: 'success',
                                          title: 'Copied',
                                          message: `Resource ID copied: ${resource.resourceId}`
                                        });
                                      }
                                    }}
                                    sx={{ cursor: resource.resourceId && resource.resourceId !== 'N/A' ? 'pointer' : 'default' }}
                                  />
                                  {resource.resourceType && (
                                    <Chip 
                                      label={resource.resourceType}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  {resource.resourceRegion && (
                                    <Chip 
                                      label={resource.resourceRegion}
                                      size="small"
                                      variant="outlined"
                                    />
                                  )}
                                  {resource.message && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                      {resource.message}
                                    </Typography>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          </Box>

                          {/* Remediation Steps - Only show for failed checks */}
                          {groupedFinding.resources.some(r => r.status === 'FAIL') && groupedFinding.remediationSteps && groupedFinding.remediationSteps.length > 0 && (
                            <Accordion sx={{ mt: 2, bgcolor: 'background.paper' }}>
                              <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Security fontSize="small" color="primary" />
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Remediation Steps
                                  </Typography>
                                  {groupedFinding.remediationComplexity && (
                                    <Chip 
                                      label={groupedFinding.remediationComplexity}
                                      size="small"
                                      sx={{
                                        bgcolor: 
                                          groupedFinding.remediationComplexity === 'LOW' ? '#4caf50' :
                                          groupedFinding.remediationComplexity === 'MEDIUM' ? '#ff9800' : '#f44336',
                                        color: '#fff'
                                      }}
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                  {groupedFinding.estimatedRemediationTime && (
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                      Est. Time: {groupedFinding.estimatedRemediationTime}
                                    </Typography>
                                  )}
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                    Follow these steps to fix this issue:
                                  </Typography>
                                  <List dense>
                                    {groupedFinding.remediationSteps.map((step, stepIndex) => (
                                      <ListItem key={stepIndex}>
                                        <ListItemIcon sx={{ minWidth: 32 }}>
                                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                            {stepIndex + 1}.
                                          </Typography>
                                        </ListItemIcon>
                                        <ListItemText 
                                          primary={step}
                                          primaryTypographyProps={{ variant: 'body2' }}
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                  
                                  {groupedFinding.awsDocumentationUrl && (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<Description />}
                                      href={groupedFinding.awsDocumentationUrl}
                                      target="_blank"
                                      sx={{ mt: 2 }}
                                    >
                                      View AWS Documentation
                                    </Button>
                                  )}
                                  
                                  {groupedFinding.remediationImpact && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                      <Typography variant="caption">
                                        <strong>Impact:</strong> {groupedFinding.remediationImpact}
                                      </Typography>
                                    </Alert>
                                  )}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </List>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>
    );
  };

  const renderInfoDialog = () => (
    <Dialog 
      open={showInfoDialog} 
      onClose={() => setShowInfoDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ 
        bgcolor: 'primary.main', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <Info />
        Regulatory Compliance Scanning Information
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          What is Regulatory Compliance Scanning?
        </Typography>
        <Typography variant="body1" paragraph>
          Regulatory compliance scanning automatically evaluates your AWS infrastructure against industry-specific 
          security and regulatory requirements. Each framework contains hundreds of security checks 
          tailored to specific industries and regulatory standards.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
          How it Works
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Select Framework" 
              secondary="Choose the compliance framework relevant to your industry"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Automated Scanning" 
              secondary="Our engine performs comprehensive security checks across your AWS account"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Detailed Reports" 
              secondary="Receive detailed compliance reports with remediation guidance"
            />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
            <ListItemText 
              primary="Continuous Monitoring" 
              secondary="Schedule regular scans to maintain compliance posture"
            />
          </ListItem>
        </List>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 3 }}>
          Supported Frameworks
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We support major compliance frameworks including SOC 2, PCI DSS, HIPAA, GDPR, ISO 27001, 
          NIST Cybersecurity Framework, FedRAMP, and CIS Controls. Each framework is regularly updated 
          to reflect the latest requirements and best practices.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={() => setShowInfoDialog(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 0 }}>
      {/* Hero Section */}
      <Box sx={{ mb: 6 }}>
        <Stack spacing={2} sx={{ mb: 4 }}>
          <Box display="flex" alignItems="center" gap={3}>
            <Avatar sx={{ 
              width: 64, 
              height: 64,
              background: 'linear-gradient(135deg, #e91e63 0%, #ad1457 100%)',
              boxShadow: '0 8px 16px rgba(233, 30, 99, 0.3)'
            }}>
              <Gavel sx={{ fontSize: 32 }} />
            </Avatar>
            <Box>
              <Typography variant="h3" sx={{ 
                fontWeight: 800, 
                background: 'linear-gradient(135deg, #e91e63 0%, #ad1457 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 0.5
              }}>
                Regulatory Compliance Scanning
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                Industry-specific compliance assessments for your AWS infrastructure
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto' }}>
              <Tooltip title="Learn about Regulatory Compliance Scanning">
                <IconButton 
                  onClick={() => setShowInfoDialog(true)}
                  sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    width: 48,
                    height: 48,
                    '&:hover': { 
                      bgcolor: 'primary.dark',
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                >
                  <Info />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Stack>

        <Alert 
          severity="info" 
          sx={{ 
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'primary.light',
            bgcolor: 'primary.50'
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            <strong>Industry-Specific Compliance:</strong> Select the compliance framework that matches your industry requirements. 
            Each framework contains hundreds of AWS-specific security checks based on official compliance standards and best practices.
          </Typography>
        </Alert>
      </Box>

      {/* Account Selection */}
      {awsAccounts.length > 0 && (
        <Card sx={{ mb: 4, borderRadius: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Security color="primary" />
              AWS Account Selection
            </Typography>
            <FormControl sx={{ minWidth: 400 }}>
              <InputLabel>Select Target Account</InputLabel>
              <Select
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e.target.value)}
                label="Select Target Account"
                sx={{ borderRadius: 2 }}
              >
                {awsAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {account.account_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Account ID: {account.account_id} • Region: {account.region}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>
      )}

      {/* Compliance Framework Selection */}
      <Card sx={{ mb: 4, borderRadius: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Assessment color="primary" />
            Select Compliance Framework
          </Typography>

          {/* Industry Filter Buttons */}
          <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {INDUSTRY_CATEGORIES.map((industry) => (
              <Button
                key={industry.id}
                variant={selectedIndustry === industry.id ? 'contained' : 'outlined'}
                startIcon={industry.icon}
                onClick={() => setSelectedIndustry(industry.id)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  ...(selectedIndustry === industry.id && industry.color && {
                    bgcolor: industry.color,
                    '&:hover': {
                      bgcolor: industry.color,
                      filter: 'brightness(0.9)'
                    }
                  })
                }}
              >
                {industry.name}
              </Button>
            ))}
          </Box>

          {/* Framework Count Badge */}
          <Box sx={{ mb: 2 }}>
            <Chip 
              label={`${filteredFrameworks.length} framework${filteredFrameworks.length !== 1 ? 's' : ''} available`}
              color="primary"
              variant="outlined"
              size="small"
            />
          </Box>
          
          <Grid container spacing={3}>
            {filteredFrameworks.map((framework) => (
              <Grid item xs={12} md={6} lg={4} key={framework.framework_id}>
                {renderComplianceFrameworkCard(framework)}
              </Grid>
            ))}
          </Grid>

          {selectedCompliance && (
            <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
              {(() => {
                const selectedFramework = frameworks.find(f => f.framework_id === selectedCompliance);
                const hasDownloadedChecks = selectedFramework?.downloaded_checks > 0;
                
                return (
                  <>
                    {!hasDownloadedChecks && (
                      <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="body2">
                          <strong>Download Required:</strong> Please download official compliance checks before starting the scan.
                        </Typography>
                      </Alert>
                    )}
                    
                    {/* Last Scan Timestamp Badge */}
                    {lastScanTimestamp && (
                      <Box sx={{ 
                        mb: 2, 
                        p: 2, 
                        bgcolor: 'success.50', 
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'success.light',
                        display: 'inline-block'
                      }}>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600, color: 'success.dark' }}>
                          <CheckCircle fontSize="small" />
                          Last Scan: {formatTimestamp(lastScanTimestamp)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFullTimestamp(lastScanTimestamp)}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={isScanning ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                        onClick={startComplianceScan}
                        disabled={isScanning || !selectedCompliance || !selectedAccount || !hasDownloadedChecks}
                        sx={{ borderRadius: 2, minWidth: 200 }}
                      >
                        {isScanning ? 'Scanning AWS Resources...' : 'Start Regulatory Compliance Scan'}
                      </Button>
                      
                      {/* Scan History Button */}
                      {scanHistory.length > 0 && (
                        <Tooltip title="View Scan History">
                          <IconButton
                            onClick={() => setShowScanHistory(true)}
                            sx={{ 
                              bgcolor: 'primary.50',
                              '&:hover': { bgcolor: 'primary.100' }
                            }}
                          >
                            <Badge badgeContent={scanHistory.length} color="primary" max={99}>
                              <History color="primary" />
                            </Badge>
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    
                    {hasDownloadedChecks && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Ready to scan with {selectedFramework.downloaded_checks} official compliance checks
                      </Typography>
                    )}
                  </>
                );
              })()}
              
              {isScanning && (
                <Box sx={{ mt: 2, maxWidth: 400 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Scanning progress: {Math.round(scanProgress)}%
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={scanProgress} 
                    sx={{ borderRadius: 1, height: 8 }}
                  />
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Scan Results */}
      {renderScanResults()}

      {/* Troubleshooting Section - Show when there are no results but user has tried scanning */}
      {!scanResults && !isScanning && selectedCompliance && (
        <Card sx={{ mt: 4, borderRadius: 4, border: '1px solid', borderColor: 'warning.light' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Warning color="warning" />
              AWS API Troubleshooting
            </Typography>
            
            <Typography variant="body1" paragraph>
              If your regulatory compliance scan fails, here are common issues and solutions:
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="AWS Credentials Configuration" 
                  secondary="Ensure AWS credentials are configured via IAM roles, environment variables, or AWS CLI"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="IAM Permissions" 
                  secondary="Verify the AWS account has read permissions for EC2, S3, RDS, IAM, CloudTrail, and other services"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="Network Connectivity" 
                  secondary="Check that the server can reach AWS APIs (outbound HTTPS on port 443)"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle color="primary" /></ListItemIcon>
                <ListItemText 
                  primary="AWS Region Access" 
                  secondary="Ensure the specified AWS region is accessible and contains resources to scan"
                />
              </ListItem>
            </List>
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>For detailed error information:</strong> Check the browser console (F12) for specific error messages and troubleshooting steps.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Info Dialog */}
      {renderInfoDialog()}

      {/* Scan History Dialog */}
      <Dialog 
        open={showScanHistory} 
        onClose={() => setShowScanHistory(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <History color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Regulatory Compliance Scan History
            </Typography>
            <Chip 
              label={`${scanHistory.length} scan${scanHistory.length !== 1 ? 's' : ''}`} 
              size="small" 
              color="primary"
            />
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {scanHistory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <History sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No scan history yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Run your first regulatory compliance scan to start tracking history
              </Typography>
            </Box>
          ) : (
            <List>
              {scanHistory.map((scan, index) => (
                <React.Fragment key={scan.id}>
                  <ListItem
                    button
                    onClick={() => loadScanFromHistory(scan)}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      border: '1px solid',
                      borderColor: index === 0 ? 'primary.main' : 'divider',
                      bgcolor: index === 0 ? 'primary.50' : 'background.paper',
                      '&:hover': {
                        bgcolor: index === 0 ? 'primary.100' : 'action.hover'
                      }
                    }}
                  >
                    <ListItemIcon>
                      <Avatar sx={{ bgcolor: index === 0 ? 'primary.main' : 'grey.400' }}>
                        <Assessment />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {scan.frameworkName}
                          </Typography>
                          {index === 0 && (
                            <Chip label="Latest" size="small" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            <AccessTime fontSize="inherit" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                            {formatTimestamp(scan.timestamp)} • {scan.accountName}
                          </Typography>
                          <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                            <Chip 
                              label={`Compliance: ${scan.summary.complianceScore}%`}
                              size="small"
                              sx={{
                                bgcolor: scan.summary.complianceScore >= 80 ? '#4caf50' : scan.summary.complianceScore >= 60 ? '#ff9800' : '#f44336',
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                            <Chip 
                              label={`${scan.summary.passed} Passed`}
                              size="small"
                              sx={{
                                bgcolor: '#4caf50',
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                            <Chip 
                              label={`${scan.summary.failed} Failed`}
                              size="small"
                              sx={{
                                bgcolor: '#f44336',
                                color: 'white',
                                fontWeight: 600
                              }}
                            />
                            {scan.summary.warnings > 0 && (
                              <Chip 
                                label={`${scan.summary.warnings} Warnings`}
                                size="small"
                                sx={{
                                  bgcolor: '#ff9800',
                                  color: 'white',
                                  fontWeight: 600
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="Download as PDF">
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadHistoricalCompliancePDF(scan);
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <FileDownload />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download as CSV">
                        <IconButton 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            downloadHistoricalComplianceCSV(scan);
                          }}
                          sx={{ color: 'success.main' }}
                        >
                          <Description />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Load this scan">
                        <IconButton 
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            loadScanFromHistory(scan);
                          }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete this scan">
                        <IconButton 
                          size="small"
                          onClick={(e) => deleteScanFromHistory(scan.scanId, e)}
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  {index < scanHistory.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setShowScanHistory(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Compliance;